import React, {useState} from 'react';
import {ScrollMode, Viewer, Worker} from '@react-pdf-viewer/core';
import Mark from 'mark.js';
// Plugins

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import './PDFViewer.css';
// Create new plugin instance
import {scrollModePlugin} from '@react-pdf-viewer/scroll-mode';
import {bookmarkPlugin} from '@react-pdf-viewer/bookmark';
import {
  toolbarPlugin,
  ToolbarSlot,
  TransformToolbarSlot
} from '@react-pdf-viewer/toolbar';
import EncryptUtils from 'src/utils/EncryptUtils';
import {SEARCH_CONTEXT} from 'src/routes/BaseRoute';
import {pageNavigationPlugin} from '@react-pdf-viewer/page-navigation';
import {Button, Tooltip, TextField} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SnackbarConfig from '@components/snackbar/SnackbarConfig';
import {useSnackbar} from 'notistack';
import {pdfWorkerUrl} from 'src/utils/pdfWorker';
interface PdfViewerProps {
  buffer: string;
}

const PDFFilecabinetViewer: React.FC<PdfViewerProps> = ({buffer}) => {
  const [urlpdf, setUrl] = useState(buffer);
  const toolbarPluginInstance = toolbarPlugin();
  const {renderDefaultToolbar, Toolbar} = toolbarPluginInstance;
  const [goToValue, setGoToValue] = useState('');
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(1);
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const {jumpToPage, jumpToPreviousPage, jumpToNextPage} =
    pageNavigationPluginInstance;
  const {enqueueSnackbar} = useSnackbar();
  const handleGoToPage = React.useCallback(() => {
    const pageNumber = Number(goToValue);
    if (pageNumber >= 1 && pageNumber <= total) {
      jumpToPage(pageNumber - 1);
      return;
    }
    enqueueSnackbar(
      'Please enter a valid page number.',
      SnackbarConfig.getError()
    );
  }, [enqueueSnackbar, goToValue, jumpToPage, total]);

  const base64toBlob = (data: string) => {
    const base64Data = data.includes('base64,')
      ? data.split('base64,')[1]
      : data;
    const byteCharacters = atob(base64Data);
    const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], {type: 'application/pdf'});
  };
  // const blob = base64toBlob(buffer);
  // const url = URL.createObjectURL(blob);
  const url = React.useMemo(() => {
    const blob = base64toBlob(buffer);
    return URL.createObjectURL(blob);
  }, [buffer]);

  const scrollModePluginInstance = scrollModePlugin();
  const bookmarkPluginInstance = bookmarkPlugin();
  const search_context = EncryptUtils.getDecryptedData(
    location?.search,
    SEARCH_CONTEXT
  );

  const handlePageRenderSuccess = () => {
    const textLayerElements = document.querySelectorAll(
      '.rpv-core__text-layer'
    );
    if (textLayerElements.length > 0 && search_context) {
      textLayerElements.forEach(el => {
        const markInstance = new Mark(el as HTMLElement);
        markInstance.unmark({
          done: () => {
            markInstance.mark(search_context, {
              separateWordSearch: false,
              accuracy: 'partially'
            });
          }
        });
      });
    }
  };

  const transform: TransformToolbarSlot = (slot: ToolbarSlot) => {
    const {NumberOfPages} = slot;
    return {
      ...slot,
      NumberOfPages: () => (
        <>
          of <NumberOfPages />
        </>
      ),

      Download: () => <></>,
      EnterFullScreen: () => <></>,
      SwitchTheme: () => <></>,
      Print: () => <></>,
      ShowProperties: () => <></>,
      ShowPropertiesMenuItem: () => <></>,
      SwitchScrollMode: () => <></>, // hides the toolbar button (if present)
      SwitchScrollModeMenuItem: () => <></>, // hides it from “More actions”
      switchViewMode: () => <></>,
      SwitchViewModeMenuItem: () => <></>,
      // OpenMenuItem:()=><>/</>
      Open: () => <></>,
      OpenMenuItem: () => <></>,
      PrintMenuItem: () => <></>,
      Search: () => <></>,
      RotateForwardMenuItem: () => <></>,
      RotateBackwardMenuItem: () => <></>,
      SwitchThemeMenuItem: () => <></>,
      ShowSearchPopover: () => <></>,
      EnterFullScreenMenuItem: () => <></>
    };
  };

  return (
    <>
      {url && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '66vh'
          }}>
          <div
            className="my-pdf-viewer"
            style={{
              alignItems: 'center',
              backgroundColor: '#ffffffff',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              display: 'flex',
              padding: '8px',
              flex: '0 0 auto'
            }}>
            <Toolbar>
              {(props: ToolbarSlot) => {
                const {Zoom, ZoomIn, ZoomOut} = props;
                return (
                  <>
                    <div
                      onMouseDown={e => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                      <Button
                        variant="outlined"
                        aria-label="Previous page"
                        disabled={current <= 1}
                        onClick={jumpToPreviousPage}
                        sx={{
                          minWidth: 32,
                          height: 32,
                          padding: 0,
                          borderRadius: '6px'
                        }}>
                        <ArrowBackIosNewIcon sx={{fontSize: 16}} />
                      </Button>
                      <div
                        style={{
                          padding: '4px 10px',
                          background: '#fff',
                          borderRadius: 6,
                          border: '1px solid #ccc',
                          fontSize: 13
                        }}>
                        Page {current} / {total}
                      </div>
                      <Button
                        variant="outlined"
                        aria-label="Next page"
                        disabled={current >= total}
                        onClick={jumpToNextPage}
                        sx={{
                          minWidth: 32,
                          height: 32,
                          padding: 0,
                          borderRadius: '6px'
                        }}>
                        <ArrowForwardIosIcon sx={{fontSize: 16}} />
                      </Button>
                      {/* <Typography>Go to page:</Typography> */}
                      <TextField
                        size="small"
                        type="number"
                        id="outlined-basic"
                        label="Go to"
                        variant="outlined"
                        inputProps={{
                          min: 1,
                          max:total
                        }}
                        value={goToValue}
                        onChange={e => {
                          const v = e.target.value;
                          if (v === '0') return;
                          if (v === '' || /^\d+$/.test(v)) setGoToValue(v);
                        }}
                        onKeyDown={e => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          e.stopPropagation();
                          if (goToValue.trim() !== '') {
                            handleGoToPage();
                          }
                        }}
                        style={{
                          fontSize: 13,
                           width: 80
                        }}
                      />
                      <Button
                        variant="outlined"
                        disabled={goToValue.trim() === ''}
                        sx={{
                          minWidth: 35,
                          height: 35,
                          padding: 0,
                          backgroundColor: goToValue
                            ? 'primary.main'
                            : 'transparent',
                          color: goToValue ? '#fff' : 'rgba(0, 0, 0, 0.38)',
                          border: goToValue
                            ? 'none'
                            : '1px solid rgba(0, 0, 0, 0.23)',
                          '&:hover': {
                            backgroundColor: goToValue
                              ? 'primary.dark'
                              : 'transparent'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'transparent'
                          }
                        }}
                        onClick={handleGoToPage}>
                        <SearchIcon sx={{fontSize: 18}} />
                      </Button>

                      <Tooltip title="Reset to first page" arrow>
                        <Button
                          variant="contained"
                          onClick={() => {
                            jumpToPage(0);
                            setCurrent(1);
                            setGoToValue('');
                          }}
                          sx={{
                            minWidth: 40,
                            height: 36,
                            borderRadius: '8px',
                            background: '#e0e0e0',
                            color: '#333',
                            textTransform: 'none',
                            '&:hover': {background: '#d5d5d5'}
                          }}>
                          <RestartAltIcon sx={{fontSize: 20}} />
                        </Button>
                      </Tooltip>
                      <ZoomOut />
                      <Zoom />
                      <ZoomIn />
                    </div>
                  </>
                );
              }}
            </Toolbar>
          </div>
          <div
            style={{
              display: 'flex',
              flex: '1 1 auto',
              minHeight: 0, // critical in flex layouts
              overflow: 'hidden' // don't scroll here
            }}>
            <Worker workerUrl={pdfWorkerUrl}>
              <Viewer
                fileUrl={url}
                plugins={[
                  bookmarkPluginInstance,
                  scrollModePluginInstance,
                  toolbarPluginInstance,
                  pageNavigationPluginInstance
                ]}
                onPageChange={e => {
                  setCurrent(e.currentPage + 1);
                }}
                onDocumentLoad={e => {
                  setTimeout(() => {
                    setTotal(e.doc.numPages);
                    handlePageRenderSuccess();
                  }, 100);
                }}
                scrollMode={ScrollMode.Vertical}
              />
            </Worker>
          </div>
        </div>
      )}
    </>
  );
};

export default PDFFilecabinetViewer;
